import React, { useEffect, useRef } from "react";
import styles from "./pageNotFound.module.scss";
import GaImage from "../../components/atoms/Image/Image";
import { gaImages } from "../../assets/globalConstants";
import cx from "classnames";
import { Button } from "@gataca/design-system/web";
import { useLocation, useNavigate } from "react-router-dom";
import { handleRedirect } from "../../utils/navigationUtils";
import { useTranslation } from "react-i18next";

type IPageNotFoundProps = {};

const PageNotFound: React.FC<IPageNotFoundProps> = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();

  // Consts
  const storedRoute = sessionStorage?.getItem("ga__route");
  const hideGoBackButton = storedRoute?.includes("/badges/");

  // Refs
  const galaxyRef = useRef<HTMLDivElement | null>(null);
  const galaxyImgRef = useRef<HTMLImageElement | null>(null);
  const blackHoleRef = useRef<HTMLDivElement | null>(null);
  const blackHoleImgRef = useRef<HTMLImageElement | null>(null);

  // Functions
  const handleImageLoad = (
    ref?: React.MutableRefObject<HTMLDivElement | null>
  ) => {
    if (ref?.current) {
      ref?.current.classList.add(
        styles.pageNotFoundContainer__imgContainer__lightgrayBG
      );
    }
  };

  // Effects
  // Galaxy img - lazy load
  useEffect(() => {
    if (galaxyImgRef?.current) {
      if (galaxyImgRef?.current?.complete) {
        handleImageLoad(galaxyRef);
      } else {
        galaxyImgRef?.current.addEventListener("load", () =>
          handleImageLoad(galaxyRef)
        );
      }
    }

    return () => {
      if (galaxyImgRef?.current) {
        galaxyImgRef?.current?.removeEventListener("load", () =>
          handleImageLoad(galaxyRef)
        );
      }
    };
  }, []);

  // Blackhole img - lazy load
  useEffect(() => {
    if (blackHoleImgRef?.current) {
      if (blackHoleImgRef?.current?.complete) {
        handleImageLoad(blackHoleRef);
      } else {
        blackHoleImgRef?.current.addEventListener("load", () =>
          handleImageLoad(blackHoleRef)
        );
      }
    }

    return () => {
      if (blackHoleImgRef?.current) {
        blackHoleImgRef?.current?.removeEventListener("load", () =>
          handleImageLoad(blackHoleRef)
        );
      }
    };
  }, []);

  return (
    <div className={styles.pageNotFoundContainer}>
      <div className={styles.pageNotFoundContainer__galaxy} ref={galaxyRef}>
        {/* Children img used only to know when bg image is loaded */}
        <GaImage
          ref={galaxyImgRef}
          className={cx("lazyLoadingImg")}
          image={gaImages.notFoundPageBG}
        />
      </div>
      <div
        className={styles.pageNotFoundContainer__blackHole}
        ref={blackHoleRef}>
        <GaImage
          ref={blackHoleImgRef}
          className={cx("lazyLoadingImg")}
          image={gaImages.notFoundPageBG}
        />
      </div>
      <div className={styles.pageNotFoundContainer__astronaut}>
        <GaImage image={gaImages.astronaut3D} />
      </div>
      <div className={styles.pageNotFoundContainer__logo}>
        <GaImage image={gaImages.gatacaLogoWhite} />
      </div>
      <div className={styles.pageNotFoundContainer__body}>
        <div className={styles.pageNotFoundContainer__body__section}>
          <p className={cx("heading1 neutral100")}>{t("notFoundPage.title")}</p>
          <p className={cx("bodyRegularXL neutral100")}>
            {t("notFoundPage.description")}
          </p>
          {!hideGoBackButton ? (
            <Button
              className={cx(
                styles.pageNotFoundContainer__body__section__button
              )}
              text={t("notFoundPage.buttonLabel")}
              style="elevated"
              color="purple"
              textSize="medium"
              onClick={() => handleRedirect(navigate, location)}
            />
          ) : null}
        </div>
      </div>
      <div></div>
    </div>
  );
};

PageNotFound.defaultProps = {};

export default PageNotFound;
